import { getCookie } from "@insite/client-framework/Common/Cookies";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import logger from "@insite/client-framework/Logger";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setSignInOverlayIsOpen from "@insite/client-framework/Store/Components/SignInOverlay/Handlers/SetSignInOverlayIsOpen";
import {
    getCurrentUserIsGuest,
    getIsPunchOutSession,
    getSession,
    getSettingsCollection,
} from "@insite/client-framework/Store/Context/ContextSelectors";
import cancelPunchOut from "@insite/client-framework/Store/Context/Handlers/CancelPunchOut";
import setShowInventory from "@insite/client-framework/Store/Context/Handlers/SetShowInventory";
import setShowPricing from "@insite/client-framework/Store/Context/Handlers/SetShowPricing";
import signOut from "@insite/client-framework/Store/Context/Handlers/SignOut";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import setIsReorderConfirmationModalOpen from "@insite/client-framework/Store/Pages/VmiBins/Handlers/SetIsReorderConfirmationModalOpen";
import setNextAction from "@insite/client-framework/Store/Pages/VmiBins/Handlers/SetNextAction";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import { NavigationModeCookieName } from "@insite/content-library/Components/NavigationModeMenu";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem from "@insite/mobius/GridItem";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon/Icon";
import Eye from "@insite/mobius/Icons/Eye";
import EyeOff from "@insite/mobius/Icons/EyeOff";
import User from "@insite/mobius/Icons/User";
import Link from "@insite/mobius/Link";
import Menu, { MappedLink } from "@insite/mobius/Menu";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import SalesUserIcon from "@tKgpco/Icons/SalesUserIcon";
import SignInIcon from "@tKgpco/Icons/SignInIcon";
import { COLORS } from "@tKgpco/theme/design";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";

const enum fields {
    visibilityState = "visibilityState",
    icon = "icon",
    includeAccountMenu = "includeAccountMenu",
    showPricingToggle = "showPricingToggle",
    showInventoryToggle = "showInventoryToggle",
}
const includeField = ["Accessorials", "Terms", "Security Roles", "Add Security Role", "Edit Security Role"];
const includeRole = ["Administrator"];
const mapStateToProps = (state: ApplicationState) => ({
    isSignInPage: getCurrentPage(state).type === "SignInPage",
    userName: state.context.session.userName,
    myAccountPageLink: getPageLinkByPageType(state, "MyAccountPage"),
    signInUrl: getPageLinkByPageType(state, "SignInPage")?.url,
    currentUserIsGuest: getCurrentUserIsGuest(state),
    isPunchOutSession: getIsPunchOutSession(state),
    currentLocation: getLocation(state),
    displayChangeCustomerLink: state.context.session?.displayChangeCustomerLink || false,
    userSession: state.context.session,
    context: state.context,
    settings: state.context.settings,
    displayPricingAndInventory: state.context.session?.displayPricingAndInventory || false,
    showInventory: state.context.showInventory,
    showPricing: state.context.showPricing,
    isReorderConfirmationModalOpen: state.pages.vmiBins.isReorderConfirmationModalOpen,
    hasChangeLastQty:
        state.pages.vmiBins.selectVmiBinsChangedQty &&
        Object.keys(state.pages.vmiBins.selectVmiBinsChangedQty).length > 0,
    orderConfirmationPageUrl: getPageLinkByPageType(state, "OrderConfirmationPage")?.url,
    onePageCheckoutConfirmationPageUrl: getPageLinkByPageType(state, "OnePageCheckoutConfirmationPage")?.url,
    fieldstoHide: state.context.settings.properties.fieldsToHide || "",
    canshowPrice: state.context?.session?.properties?.canShowPrice?.toLowerCase() === "true",
    featurePages: state.context.session.properties?.featurePages?.split(",").map(page => page.trim()) || [],
    ootbFeaturePages: state?.context?.session?.properties?.ootbFeaturePages?.split(",").map(page => page.trim()) || [],
    currentBillTo: getBillToState(state, getSession(state).billToId).value,
    isBuyer3: state.context.session?.userRoles?.toLowerCase()?.includes("buyer3") || false,
});

const mapDispatchToProps = {
    signOut,
    cancelPunchOut,
    setSignInOverlayIsOpen,
    setShowInventory,
    setShowPricing,
    setIsReorderConfirmationModalOpen,
    setNextAction,
};

export interface HeaderSignInStyles {
    signOutWrapper?: InjectableCss;
    titleClickable?: ClickablePresentationProps;
    titleIcon?: IconPresentationProps;
    titleTypography?: TypographyPresentationProps;
    signOutClickable?: ClickablePresentationProps;
    myAccountMenu?: GridContainerProps;
    myAccountMenuList?: InjectableCss;
    myAccountMenuLink?: InjectableCss;
    openMyAccountMenu?: InjectableCss;
    signOutClickableWrapper?: InjectableCss;
}

export const headerSignInStyles: HeaderSignInStyles = {
    signOutWrapper: {
        css: css`
            display: flex;
            button {
                margin-left: 0;
                font-size: 13px;
            }
            nav {
                &:hover {
                    span,
                    p {
                        color: hsl(0, 0%, 70%);
                    }
                }
            }
        `,
    },
    titleClickable: {
        css: css`
            margin-left: 0;
            flex-direction: column;
        `,
    },
    titleIcon: {
        size: 22,
        css: css`
            margin-right: 5px;
        `,
    },
    titleTypography: {
        css: css`
            max-width: 100px;
            white-space: nowrap;
            text-overflow: ellipsis;
            overflow: hidden;
            margin-top: 9px;
            font-size: 11px;
        `,
        ellipsis: true,
        transform: "capitalize",
    },
    signOutClickableWrapper: {
        css: css`
            padding: 6px 15px 6px 20px;
            & button {
                font-size: 15px !important;
                font-weight: 400;
                text-transform: capitalize;
                color: #0f0f0f;
            }
            &:hover {
                background: #cccccc;
            }
        `,
    },
    signOutClickable: {
        css: css`
            flex: 0 0 auto;
            margin-left: 16px;
            text-transform: uppercase;
            font-weight: 500;
            text-align: left;
        `,
    },
    myAccountMenu: {
        css: css`
            display: none;
        `,
    },
    myAccountMenuList: {
        css: css`
            width: 100%;
            padding: 5px 0;
            max-width: 100%;
            &:hover {
                background: #cccccc;
            }
        `,
    },
    myAccountMenuLink: {
        css: css`
            display: inline-flex;
            margin: 0;
            padding: 0;
            text-align: left;
            span {
                padding: 2px 14px 2px 20px;
                color: #0f0f0f !important;
                font-size: 15px !important;
            }
        `,
    },
    openMyAccountMenu: {
        css: css`
            display: block;
            background: ${COLORS.white};
            box-shadow:
                0px 2px 4px -1px rgba(0, 0, 0, 0.2),
                0px 4px 5px 0px rgba(0, 0, 0, 0.14),
                0px 1px 10px 0px rgba(0, 0, 0, 0.12);
            padding: 0;
            position: absolute;
            right: 1%;
            width: 190px;
            z-index: 150;
            max-height: 80vh;
            height: auto;
            min-height: 15vh;
            overflow: auto;
            &::-webkit-scrollbar {
                width: 5px;
            }

            /* Track */
            &::-webkit-scrollbar-track {
                background: #f1f1f1;
            }

            /* Handle */
            &::-webkit-scrollbar-thumb {
                background: #c1c1c1;
            }

            /* Handle on hover */
            &::-webkit-scrollbar-thumb:hover {
                background: #555555;
            }
            ul {
                display: block !important;
                width: 100% !important;
                margin: 0;
                padding: 0;
                li {
                    a {
                        span {
                            font-size: 13px;
                        }
                    }
                }
            }
        `,
    },
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.visibilityState]: string;
        [fields.icon]: string;
        [fields.includeAccountMenu]: boolean;
        [fields.showPricingToggle]: boolean;
        [fields.showInventoryToggle]: boolean;
    };
}

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasHistory &
    ThemeProps<BaseTheme>;

let icon: React.ComponentType<IconPresentationProps>;
export const HeaderSignIn = ({
    userName,
    isSignInPage,
    currentUserIsGuest,
    signOut,
    cancelPunchOut,
    fields,
    history,
    myAccountPageLink,
    signInUrl,
    isPunchOutSession,
    currentLocation,
    displayChangeCustomerLink,
    userSession,
    settings,
    displayPricingAndInventory,
    showInventory,
    showPricing,
    setSignInOverlayIsOpen,
    setShowInventory,
    setShowPricing,
    theme,
    isReorderConfirmationModalOpen,
    hasChangeLastQty,
    setIsReorderConfirmationModalOpen,
    setNextAction,
    orderConfirmationPageUrl,
    onePageCheckoutConfirmationPageUrl,
    canshowPrice,
    fieldstoHide,
    featurePages,
    ootbFeaturePages,
    currentBillTo,
    isBuyer3,
}: Props) => {
    const styles = useMergeStyles("headerSignIn", headerSignInStyles);
    const { visibilityState, includeAccountMenu, showInventoryToggle, showPricingToggle } = fields;
    const showIcon = visibilityState === "both" || visibilityState === "icon";
    const showLabel = visibilityState === "both" || visibilityState === "label";
    const [hovered, setHovered] = useState(false);
    const hoverIn = () => setHovered(true);
    const hoverOut = () => setHovered(false);
    const { properties } = settings;

    useEffect(() => {
        window.onpageshow = function (event) {
            if (event.persisted) {
                window.location.reload();
            }
        };
    }, [userName]);

    if (!myAccountPageLink) {
        return null;
    }
    if (fields.icon === "User") {
        icon = User;
    }

    const currentMode = getCookie(NavigationModeCookieName);
    let menuItems = (myAccountPageLink?.children
        ?.filter(child => {
            const title = child?.title?.trim();

            // Safely split and normalize excluded titles
            const excludedTitles =
                !canshowPrice && fieldstoHide ? fieldstoHide.split(",").map(t => t.trim().toLowerCase()) : [];

            if (!title) {
                return true;
            }

            return !excludedTitles.includes(title.toLowerCase());
        })
        ?.filter(pageLink => !(pageLink.type === "ChangeCustomerPage" && !displayChangeCustomerLink)) ??
        []) as MappedLink[];

    const getMenuItem = (title: "Inventory" | "Pricing"): MappedLink => {
        return {
            title: title === "Inventory" ? translate("Inventory") : translate("Pricing"),
            iconProps: {
                src: (title === "Inventory" && showInventory) || (title === "Pricing" && showPricing) ? EyeOff : Eye,
                size: 22,
                css: css`
                    margin-left: 20px;
                    margin-right: -12px;
                `,
            },
            clickableProps: {
                css: css`
                    display: flex;
                    text-transform: uppercase;
                `,
                onClick: () => {
                    title === "Inventory"
                        ? setShowInventory({ showInventory: !showInventory })
                        : setShowPricing({ showPricing: !showPricing });
                },
            },
        };
    };

    if (!currentMode || currentMode === "Storefront") {
        menuItems = menuItems?.filter(pageLink => pageLink.type !== "VmiUsersPage");

        showInventoryToggle && displayPricingAndInventory && menuItems.push(getMenuItem("Inventory"));
        showPricingToggle && displayPricingAndInventory && menuItems.push(getMenuItem("Pricing"));
    }

    const onSignInHandler = (e: React.MouseEvent) => {
        if ((!userName || currentUserIsGuest) && !isSignInPage) {
            e.preventDefault();

            if (theme.signIn?.useOverlay) {
                setSignInOverlayIsOpen({ isOpen: true });
                return;
            }

            if (!signInUrl) {
                logger.warn("No url was found for SignInPage, defaulting to /SignIn");
                history.push("/MyAccount/SignIn");
                return;
            }

            const returnUrl = currentLocation.pathname + currentLocation.search;
            if (
                returnUrl === "/" ||
                (currentUserIsGuest && orderConfirmationPageUrl && returnUrl.indexOf(orderConfirmationPageUrl) > -1) ||
                (currentUserIsGuest &&
                    onePageCheckoutConfirmationPageUrl &&
                    returnUrl.indexOf(onePageCheckoutConfirmationPageUrl) > -1)
            ) {
                history.push(`${signInUrl}`);
                return;
            }

            history.push(`${signInUrl}?returnUrl=${encodeURIComponent(returnUrl)}&clientRedirect=true`);
        }
    };

    const onSignOutHandler = () => {
        if (!isReorderConfirmationModalOpen && hasChangeLastQty) {
            setIsReorderConfirmationModalOpen({ isReorderConfirmationModalOpen: true });
            setNextAction({ nextAction: signOutFunc });
        } else {
            signOutFunc();
        }
    };

    const signOutFunc = () => {
        isPunchOutSession ? cancelPunchOut() : signOut();
    };

    let signInStatusText = translate("Sign In");
    if (userName && !currentUserIsGuest) {
        signInStatusText = userName;
    }

    return (
        <StyledWrapper {...styles.signOutWrapper}>
            {includeAccountMenu && userName && !currentUserIsGuest && (
                <StyledWrapper>
                    {/* <Menu
                        descriptionId="accountMenu"
                        menuItems={
                        menuItems?.filter(
                            pageLink => !(pageLink.type === "ChangeCustomerPage" && !displayChangeCustomerLink),
                        ) ?? []
                    }
                    displayChangeCustomerLink={displayChangeCustomerLink}
                        maxDepth={1}
                        menuTrigger={
                            <Clickable {...styles.titleClickable} data-test-selector="header_signIn">
                                {showIcon && <Icon {...styles.titleIcon} src={icon} />}
                                {showLabel && (
                                    <Typography {...styles.titleTypography} data-test-selector="header_userName">
                                        {userName}
                                    </Typography>
                                )}
                            </Clickable>
                        }
                        cssOverrides={{
                            wrapper: css`
                                flex: 0 1 auto;
                                min-width: 0;
                            `,
                        }}
                        data-test-selector="header_accountMenu"
                    /> */}
                    <Clickable
                        {...styles.titleClickable}
                        data-test-selector="header_signIn"
                        onMouseEnter={hoverIn}
                        onMouseLeave={hoverOut}
                    >
                        {showIcon && (
                            <Icon
                                {...styles.titleIcon}
                                src={
                                    userSession.userRoles === "Administrator" || userSession.isSalesPerson
                                        ? SalesUserIcon
                                        : SignInIcon
                                }
                            />
                        )}
                        {showLabel && (
                            <Typography {...styles.titleTypography} data-test-selector="header_userName">
                                {userName}
                            </Typography>
                        )}
                    </Clickable>
                    <GridContainer
                        {...(hovered ? { ...styles.openMyAccountMenu } : { ...styles.myAccountMenu })}
                        offsetProps={{ ...styles.myAccountMenu?.offsetProps, as: "ul" }}
                        onMouseEnter={hoverIn}
                        onMouseLeave={hoverOut}
                    >
                        {myAccountPageLink?.children
                            ?.filter(child => {
                                const title = child?.title?.trim();

                                // Safely split and normalize excluded titles
                                const excludedTitles =
                                    !canshowPrice && fieldstoHide
                                        ? fieldstoHide.split(",").map(t => t.trim().toLowerCase())
                                        : [];

                                if (!title) {
                                    return true;
                                }

                                if (featurePages.includes(title)) {
                                    return false;
                                }
                                if (ootbFeaturePages.includes(title)) {
                                    return false;
                                }

                                if (
                                    title === "Requisitions" &&
                                    currentBillTo?.properties?.disableRequisitionCheckout?.toLowerCase() === "true"
                                ) {
                                    return false;
                                }

                                if (title === "Requisitions" && isBuyer3) {
                                    return false;
                                }

                                return !excludedTitles.includes(title.toLowerCase());
                            })

                            ?.filter(child => !child.excludeFromNavigation)
                            ?.filter(child => {
                                let flag = true;
                                includeRole?.forEach((role: string) => {
                                    if (includeField?.includes(child.title)) {
                                        if (
                                            userSession?.userRoles?.indexOf(role) !== -1 ||
                                            (child.title === "Terms" && userSession?.isSalesPerson)
                                        ) {
                                            flag = true;
                                        } else {
                                            flag = false;
                                        }
                                    }
                                });
                                if (child.title === "Reports") {
                                    flag = !(
                                        properties.backOrderedItemsReportsType === "OFF" &&
                                        properties.purchaseHistoryReportsType === "OFF"
                                    );
                                }
                                return flag;
                            })
                            ?.map((child, index) => (
                                <GridItem {...styles.myAccountMenuList} key={index} as="li">
                                    <Link {...styles.myAccountMenuLink} href={child.url} onClick={hoverOut}>
                                        {child.title}
                                    </Link>
                                </GridItem>
                            ))}
                        <GridItem {...styles.myAccountMenuList} as="li">
                            <Link {...styles.myAccountMenuLink} href="/QuickOrder" onClick={hoverOut}>
                                Quick Order
                            </Link>
                        </GridItem>
                        <Typography as="li" {...styles.signOutClickableWrapper}>
                            <Clickable
                                {...styles.signOutClickable}
                                onClick={signOut}
                                data-test-selector="headerSignOutLink"
                            >
                                {translate("Sign Out")}
                            </Clickable>
                        </Typography>
                    </GridContainer>
                </StyledWrapper>
            )}
            {(!includeAccountMenu || !userName || (userName && currentUserIsGuest)) && (
                <Clickable {...styles.titleClickable} onClick={onSignInHandler} data-test-selector="header_signIn">
                    {showIcon && (
                        <Icon
                            {...styles.titleIcon}
                            src={
                                userSession.userRoles === "Administrator" || userSession.isSalesPerson
                                    ? SalesUserIcon
                                    : SignInIcon
                            }
                        />
                    )}
                    {showLabel && <Typography {...styles.titleTypography}>{signInStatusText}</Typography>}
                </Clickable>
            )}
            {/* removed for signout */}
            {isPunchOutSession
                ? userName &&
                  !currentUserIsGuest && (
                      <Clickable
                          {...styles.signOutClickable}
                          onClick={onSignOutHandler}
                          data-test-selector="headerSignOutLink"
                      >
                          {isPunchOutSession ? translate("Cancel PunchOut") : null}
                      </Clickable>
                  )
                : null}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(withTheme(HeaderSignIn))),
    definition: {
        displayName: "Header Sign In",
        icon: "arrow-right-to-bracket",
        fieldDefinitions: [
            {
                name: fields.visibilityState,
                displayName: "Settings",
                editorTemplate: "RadioButtonsField",
                options: [
                    {
                        displayName: "Both",
                        value: "both",
                    },
                    {
                        displayName: "Show Label",
                        value: "label",
                    },
                    {
                        displayName: "Show Icon",
                        value: "icon",
                    },
                ],
                defaultValue: "both",
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.icon,
                displayName: "SignIn Icon",
                editorTemplate: "DropDownField",
                options: [
                    {
                        displayName: "User",
                        value: "User",
                    },
                ],
                defaultValue: "User",
                fieldType: "General",
                sortOrder: 2,
            },
            {
                name: fields.includeAccountMenu,
                displayName: "Include My Account Menu",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 3,
            },
            {
                name: fields.showPricingToggle,
                displayName: "Show/Hide Pricing Toggle",
                editorTemplate: "CheckboxField",
                isVisible: widget => widget.fields.includeAccountMenu,
                defaultValue: false,
                fieldType: "General",
                sortOrder: 4,
            },
            {
                name: fields.showInventoryToggle,
                displayName: "Show/Hide Inventory Toggle",
                editorTemplate: "CheckboxField",
                isVisible: widget => widget.fields.includeAccountMenu,
                defaultValue: false,
                fieldType: "General",
                sortOrder: 5,
            },
        ],
        group: "Common",
    },
};

export default widgetModule;
